import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useLanguage } from '../../context/LanguageContext';
import api from '../../services/api';

const SessionReview = () => {
    const { sessionId } = useParams();
    const navigate = useNavigate();
    const { language } = useLanguage();
    const [session, setSession] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        const loadSession = async () => {
            try {
                const data = await api.getSessionDetails(sessionId);
                setSession(data);
            } catch (err) {
                console.error("Failed to load session:", err);
                setError(language === 'fr' ? "Impossible de charger la session." : "Could not load session.");
            } finally {
                setLoading(false);
            }
        };
        loadSession();
    }, [sessionId, language]);

    const getLocalizedText = (textObj) => {
        if (!textObj) return '';
        if (typeof textObj === 'string') return textObj;
        return textObj[language] || textObj['en'] || '';
    };

    if (loading) {
        return (
            <div className="min-h-screen bg-gray-50 dark:bg-black p-8 flex items-center justify-center">
                <div className="text-gray-500 dark:text-gray-400">Loading review...</div>
            </div>
        );
    }

    if (error || !session) {
        return (
            <div className="min-h-screen bg-gray-50 dark:bg-black p-8 flex items-center justify-center">
                <div className="text-red-500">{error || "Session not found"}</div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gray-50 dark:bg-black p-4 md:p-8 animate-in fade-in">
            <div className="max-w-4xl mx-auto">
                <button
                    onClick={() => navigate(-1)}
                    className="mb-6 flex items-center gap-2 text-gray-500 hover:text-gray-900 dark:text-gray-400 dark:hover:text-white transition-colors"
                >
                    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                    </svg>
                    {language === 'fr' ? 'Retour' : 'Back'}
                </button>

                {/* Header Card */}
                <div className="bg-white dark:bg-[#121212] rounded-3xl p-8 border border-gray-200 dark:border-white/5 shadow-xl mb-8">
                    <h1 className="text-2xl md:text-3xl font-black text-gray-900 dark:text-white mb-2">
                        {getLocalizedText(session.examTitle)}
                    </h1>
                    <div className="flex items-center gap-4 text-sm text-gray-500 dark:text-gray-400 uppercase tracking-widest font-bold">
                        <span>{new Date(session.date).toLocaleDateString()}</span>
                        <span>•</span>
                        <span className={session.score >= 80 ? 'text-emerald-500' : 'text-orange-500'}>
                            Score: {session.score}%
                        </span>
                    </div>
                </div>

                {/* Questions List */}
                <div className="space-y-6">
                    {session.questions.map((q, idx) => (
                        <div key={q.id} className="bg-white dark:bg-[#121212] rounded-3xl p-6 md:p-8 border border-gray-200 dark:border-white/5 shadow-sm">
                            <div className="flex gap-4">
                                <div className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center text-white font-bold text-sm
                                    ${q.isCorrect ? 'bg-emerald-500' : 'bg-red-500'}
                                `}>
                                    {idx + 1}
                                </div>
                                <div className="flex-1">
                                    <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-6">
                                        {getLocalizedText(q.text)}
                                    </h3>

                                    <div className="space-y-3 mb-6">
                                        {q.options.map((opt) => {
                                            const isSelected = q.userAnswerId == opt.id; // Loose equality for potential string/int mismatch
                                            const isCorrect = opt.isCorrect;

                                            let containerClass = "border-gray-200 dark:border-white/10 bg-gray-50 dark:bg-white/5";
                                            if (isCorrect) {
                                                containerClass = "border-emerald-500 bg-emerald-500/10";
                                            } else if (isSelected && !isCorrect) {
                                                containerClass = "border-red-500 bg-red-500/10";
                                            }

                                            return (
                                                <div
                                                    key={opt.id}
                                                    className={`p-4 rounded-xl border-2 flex items-center justify-between ${containerClass}`}
                                                >
                                                    <span className={`font-medium ${isCorrect ? 'text-emerald-700 dark:text-emerald-400' : (isSelected && !isCorrect ? 'text-red-700 dark:text-red-400' : 'text-gray-700 dark:text-gray-300')}`}>
                                                        {getLocalizedText(opt.text)}
                                                    </span>
                                                    {isCorrect && (
                                                        <svg className="w-6 h-6 text-emerald-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                                        </svg>
                                                    )}
                                                    {isSelected && !isCorrect && (
                                                        <svg className="w-6 h-6 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                                        </svg>
                                                    )}
                                                </div>
                                            );
                                        })}
                                    </div>

                                    {/* Explanation */}
                                    <div className="bg-blue-50 dark:bg-blue-900/10 rounded-xl p-4 border border-blue-100 dark:border-blue-500/20">
                                        <div className="flex items-center gap-2 text-blue-600 dark:text-blue-400 font-bold uppercase text-xs tracking-widest mb-2">
                                            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                            </svg>
                                            {language === 'fr' ? 'Explication' : 'Explanation'}
                                        </div>
                                        <p className="text-gray-700 dark:text-gray-300 text-sm leading-relaxed">
                                            {getLocalizedText(q.explanation) || (language === 'fr' ? "Aucune explication disponible." : "No explanation available.")}
                                        </p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
};

export default SessionReview;
